if (!customElements.get('social-proof-notification')) {
  customElements.define(
    'social-proof-notification',
    class SocialProofNotification extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.config = {
          initialDelay: (parseInt(this.dataset.initialDelay, 10) || 5) * 1000,
          displayDuration: (parseInt(this.dataset.displayDuration, 10) || 5) * 1000,
          pauseBetween: (parseInt(this.dataset.pauseBetween, 10) || 8) * 1000,
          maxPerSession: parseInt(this.dataset.maxPerSession, 10) || 10,
          showTime: this.dataset.showTime !== 'false',
        };

        this.i18n = {
          ordered: this.dataset.tOrdered || '',
          cityPrefix: this.dataset.tCityPrefix || '',
          justNow: this.dataset.tJustNow || '',
          minutes: this.dataset.tMinutes || '{{ count }}m',
          hours: this.dataset.tHours || '{{ count }}u',
        };

        this.dismissKey = 'sleektheme:socialProofDismissed';
        this.shownCountKey = 'sleektheme:socialProofShownCount';

        if (sessionStorage.getItem(this.dismissKey) === '1') return;

        const dataEl = this.querySelector('script[data-entries]');
        if (!dataEl) return;

        let entries;
        try {
          entries = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.warn('[social-proof] failed to parse entries', e);
          return;
        }

        if (!Array.isArray(entries) || entries.length === 0) return;

        this.template = this.querySelector('template[data-template]');
        if (!this.template) return;

        this.entries = this.shuffle(entries);
        this.entryIndex = 0;
        this.currentNode = null;
        this.shownInSession = parseInt(sessionStorage.getItem(this.shownCountKey) || '0', 10);
        this.paused = false;

        this.removeAttribute('hidden');

        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.pause();
          else this.resume();
        });

        this.scheduleNext(this.config.initialDelay);
      }

      shuffle(array) {
        const result = array.slice();
        for (let i = result.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [result[i], result[j]] = [result[j], result[i]];
        }
        return result;
      }

      pickNext() {
        if (this.entryIndex >= this.entries.length) {
          this.entries = this.shuffle(this.entries);
          this.entryIndex = 0;
        }
        return this.entries[this.entryIndex++];
      }

      scheduleNext(delay) {
        clearTimeout(this.nextTimer);
        this.nextTimer = setTimeout(() => this.showNext(), delay);
      }

      showNext() {
        if (this.shownInSession >= this.config.maxPerSession) return;
        const entry = this.pickNext();
        if (!entry) return;
        this.show(entry);
      }

      show(entry) {
        if (this.currentNode) this.currentNode.remove();

        const frag = this.template.content.cloneNode(true);
        const node = frag.querySelector('.social-proof-notification__item');
        if (!node) return;

        const img = node.querySelector('.social-proof-notification__image');
        const nameEl = node.querySelector('.social-proof-notification__name');
        const subtitleEl = node.querySelector('.social-proof-notification__subtitle');
        const timeEl = node.querySelector('.social-proof-notification__time');
        const closeBtn = node.querySelector('.social-proof-notification__close');

        if (img && entry.image) {
          img.src = entry.image;
          img.alt = entry.product || '';
        }

        const cleanName = this.fixCase(entry.name);
        const cleanCity = this.fixCase(entry.city);
        const nameLine = cleanName + (cleanCity ? ' ' + this.i18n.cityPrefix + ' ' + cleanCity : '');
        if (nameEl) nameEl.textContent = nameLine;
        if (subtitleEl) subtitleEl.textContent = this.formatOrdered(entry.product);

        const relTime = this.formatRelativeTime(entry.timestamp);
        if (this.config.showTime && timeEl && relTime) {
          timeEl.textContent = relTime;
          timeEl.dateTime = new Date(entry.timestamp * 1000).toISOString();
        } else if (timeEl) {
          timeEl.remove();
        }

        if (entry.url) node.href = entry.url;
        node.setAttribute('aria-label', this.buildAriaLabel(entry, relTime));

        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.dismiss();
          });
        }

        this.appendChild(frag);
        this.currentNode = node;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.currentNode === node) {
              node.classList.add('social-proof-notification__item--visible');
            }
          });
        });

        this.shownInSession++;
        try {
          sessionStorage.setItem(this.shownCountKey, String(this.shownInSession));
        } catch (e) {}

        clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => this.hide(), this.config.displayDuration);
      }

      hide() {
        if (!this.currentNode) return;
        const nodeToRemove = this.currentNode;
        nodeToRemove.classList.remove('social-proof-notification__item--visible');
        setTimeout(() => {
          if (nodeToRemove.parentNode) nodeToRemove.remove();
          if (this.currentNode === nodeToRemove) this.currentNode = null;
        }, 400);
        if (this.shownInSession < this.config.maxPerSession) {
          this.scheduleNext(this.config.pauseBetween);
        }
      }

      dismiss() {
        try {
          sessionStorage.setItem(this.dismissKey, '1');
        } catch (e) {}
        clearTimeout(this.nextTimer);
        clearTimeout(this.hideTimer);
        if (this.currentNode) this.currentNode.remove();
        this.currentNode = null;
        this.setAttribute('hidden', '');
      }

      pause() {
        this.paused = true;
        clearTimeout(this.nextTimer);
        clearTimeout(this.hideTimer);
      }

      resume() {
        if (!this.paused) return;
        this.paused = false;
        if (!this.currentNode && this.shownInSession < this.config.maxPerSession) {
          this.scheduleNext(this.config.pauseBetween);
        }
      }

      formatRelativeTime(timestamp) {
        const diff = Math.floor(Date.now() / 1000) - timestamp;
        if (diff < 60) return this.i18n.justNow;
        if (diff < 3600) {
          const m = Math.floor(diff / 60);
          return this.replaceCount(this.i18n.minutes, m);
        }
        if (diff < 86400) {
          const h = Math.floor(diff / 3600);
          return this.replaceCount(this.i18n.hours, h);
        }
        return '';
      }

      replaceCount(template, count) {
        return template.replace(/\{\{\s*count\s*\}\}/g, count);
      }

      formatOrdered(productName) {
        return this.i18n.ordered.replace(/\{\{\s*product\s*\}\}/g, productName || '');
      }

      buildAriaLabel(entry, relTime) {
        const subtitleText = this.formatOrdered(entry.product);
        const cleanName = this.fixCase(entry.name);
        const cleanCity = this.fixCase(entry.city);
        const nameLine = cleanName + (cleanCity ? ' ' + this.i18n.cityPrefix + ' ' + cleanCity : '');
        return nameLine + ' — ' + subtitleText + (relTime ? ' (' + relTime + ')' : '');
      }

      fixCase(str) {
        if (!str) return '';
        if (str.length > 1 && str === str.toUpperCase()) {
          str = str.toLowerCase();
        }
        return str.charAt(0).toUpperCase() + str.slice(1);
      }
    }
  );
}
